// documents#637 — the store's Shopify connection, as the API reports it:
//
//   shopify_connection / shopifyConnection: {
//     status:     'CONNECTED' | 'REAUTH_REQUIRED' | 'DISCONNECTED',
//     last_ok_at: ISO | null,     // last call Shopify accepted
//     last_error: string | null,  // machine-readable reason, see REAUTH_REASON
//                                 // in api/src/utils/shopify_auth_error.js
//     reauth_url: string | null   // the Outfino app inside the merchant's admin
//   }
//
// It travels on GET /user/me → stores[].shopifyConnection (the brand selector),
// on GET /brands/:id → data.shopify_connection, and on every shape of
// GET /shipping/coverage. This module turns the block into what the banner and
// the Shipping tab render. Pure, no React — unit-testable on its own, and
// importable from ShippingTab.js without dragging the map deps into Jest.

export const CONNECTION_STATUS = Object.freeze({
  CONNECTED: 'CONNECTED',
  REAUTH_REQUIRED: 'REAUTH_REQUIRED',
  DISCONNECTED: 'DISCONNECTED',
});

/** Normalised status; anything unrecognised is treated as CONNECTED (no banner). */
export function connectionStatus(connection) {
  const raw = typeof connection?.status === 'string' ? connection.status.trim().toUpperCase() : '';
  return Object.values(CONNECTION_STATUS).includes(raw) ? raw : CONNECTION_STATUS.CONNECTED;
}

/** True when the store needs the merchant's attention (banner shown). */
export function needsAttention(connection) {
  return connectionStatus(connection) !== CONNECTION_STATUS.CONNECTED;
}

/**
 * A human hint for brands.shopify_last_error. Returns the i18n key plus the
 * interpolation vars, or null when there is nothing to explain.
 *
 *   refresh_token_expired, non_expiring_token_rejected → token expired
 *   access_token_rejected, refresh_token_missing, no_access_token,
 *   refresh_failed:*, token_exchange_failed:*          → token rejected / revoked
 *   scope_missing:<scope>                              → a permission is missing
 *   app_uninstalled                                    → the app is gone
 *   anything else                                      → shown as a raw code
 */
export function describeLastError(lastError) {
  if (!lastError || typeof lastError !== 'string') return null;
  const code = lastError.trim();
  if (!code) return null;

  if (code.startsWith('scope_missing:')) {
    return { key: 'connection.err.scopeMissing', vars: { scope: code.slice('scope_missing:'.length) || '?' } };
  }
  if (code === 'app_uninstalled') return { key: 'connection.err.uninstalled', vars: {} };
  if (code === 'refresh_token_expired' || code === 'non_expiring_token_rejected') {
    return { key: 'connection.err.tokenExpired', vars: {} };
  }
  if (
    code === 'access_token_rejected'
    || code === 'refresh_token_missing'
    || code === 'no_access_token'
    || code.startsWith('refresh_failed')
    || code.startsWith('token_exchange_failed')
  ) {
    return { key: 'connection.err.tokenRejected', vars: {} };
  }
  return { key: 'connection.err.generic', vars: { code } };
}

/**
 * Everything the banner renders for one connection block.
 *
 * @param {Object|null} connection  the shopify_connection / shopifyConnection block
 * @returns {{
 *   status: string,
 *   show: boolean,               // false for CONNECTED
 *   variant: 'warn'|'error',     // REAUTH_REQUIRED → warn, DISCONNECTED → error
 *   titleKey: string,
 *   ctaKey: string,
 *   reauthUrl: string|null,
 *   lastOkAt: Date|null,
 *   lastError: { key: string, vars: Object } | null
 * }}
 */
export function describeConnection(connection) {
  const status = connectionStatus(connection);
  const disconnected = status === CONNECTION_STATUS.DISCONNECTED;
  return {
    status,
    show: status !== CONNECTION_STATUS.CONNECTED,
    variant: disconnected ? 'error' : 'warn',
    titleKey: disconnected ? 'connection.disconnectedTitle' : 'connection.reauthTitle',
    ctaKey: disconnected ? 'connection.disconnectedCta' : 'connection.reauthCta',
    reauthUrl: typeof connection?.reauth_url === 'string' && connection.reauth_url ? connection.reauth_url : null,
    lastOkAt: parseDate(connection?.last_ok_at),
    lastError: describeLastError(connection?.last_error),
  };
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Short, locale-aware "21 Sep 2026, 14:05"; never throws.
export function formatLastOk(date) {
  if (!date) return null;
  try {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return date.toISOString();
  }
}
